import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import WithdrawPassModal from './WithdrawPassModal';
import WithdrawRejectModal from './WithdrawRejectModal';
import WithdrawDetailModal from './WithdrawDetailModal';
import WithdrawFeeDetailModal from './WithdrawFeeDetailModal';
import {
  FIRST_BINDING_OPTIONS,
  FIRST_WITHDRAW_OPTIONS,
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  WITHDRAW_STATUS_OPTIONS,
  WITHDRAW_TYPE_OPTIONS,
  buildWithdrawPageParams,
  canWithdrawLock,
  canWithdrawPassReject,
  canWithdrawUnlock,
  defaultWithdrawApplyTimeRange,
  formatAmount,
  formatFirstBinding,
  formatFirstWithdraw,
  formatOptionalId,
  formatWithdrawStatus,
  formatWithdrawType,
  validateWithdrawTimeRange,
  withdrawStatusClassName,
} from './withdrawConstants';

const DEFAULT_RANGE = defaultWithdrawApplyTimeRange();

const EMPTY_FILTERS = {
  thirdRecordCode: '',
  type: '',
  thirdName: '',
  status: '',
  roleId: '',
  topInviterId: '',
  inviterId: '',
  memberId: '',
  phoneEmail: '',
  currencyName: '',
  minWithdrawAmount: '',
  maxWithdrawAmount: '',
  departmentId: '',
  id: '',
  address: '',
  firstWithdraw: '',
  behaviorNumber: '',
  firstBindingOrModified: '',
  createTimeFromInput: DEFAULT_RANGE.createTimeFromInput,
  createTimeToInput: DEFAULT_RANGE.createTimeToInput,
  callbackTimeFromInput: '',
  callbackTimeToInput: '',
};

const TABLE_COLUMNS = [
  '頂級上級用戶ID',
  '提現單號',
  '商戶單號',
  '提現方式',
  '商戶接口',
  '審核狀態',
  '備註-拒絕理由(前台)',
  '備註(後台)',
  '帳號類型',
  '直屬上級用戶ID',
  '用戶ID',
  '取款次數',
  '是否首次提現',
  '提現地址首綁/修改',
  '提現幣種',
  '提現數量',
  '手續費',
  '提現金額',
  '到帳金額',
  '提現地址',
  '申請時間',
  '回調時間',
  '部門ID',
  '操作',
];

const EMPTY_TOTAL = {
  recordCount: 0,
  amount: 0,
  fee: 0,
  withdrawalAmount: 0,
  receivedAmount: 0,
};

export default function WithdrawListPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(EMPTY_TOTAL);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [passRow, setPassRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [feeDetailRow, setFeeDetailRow] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    const params = buildWithdrawPageParams(pageNo, nextFilters);
    try {
      const [pageData, totalData] = await Promise.all([
        adminApi.withdrawPage(params),
        adminApi.withdrawTotal(params),
      ]);
      setRecords(pageData?.records || []);
      setPage(pageData?.current || pageNo);
      setTotalPages(pageData?.pages || 1);
      setTotal(totalData || EMPTY_TOTAL);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setTotal(EMPTY_TOTAL);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function validateFilters() {
    const applyError = validateWithdrawTimeRange(
      filters.createTimeFromInput,
      filters.createTimeToInput,
      '申請時間'
    );
    if (applyError) {
      return applyError;
    }
    return validateWithdrawTimeRange(
      filters.callbackTimeFromInput,
      filters.callbackTimeToInput,
      '回調時間'
    );
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportWithdrawCsvZip({
        ...buildWithdrawPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'member_withdraw',
      });
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function handleLock(row, isLock) {
    if (!row?.id) {
      return;
    }
    setActionId(row.id);
    setError('');
    try {
      await adminApi.withdrawLock({
        id: row.id,
        isLock,
      });
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || (isLock ? '鎖定失敗' : '解鎖失敗'));
    } finally {
      setActionId(null);
    }
  }

  function handleAuditSuccess() {
    loadRecords(page, filters);
  }

  return (
    <div className="page-card">
      <h2>提現列表</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="wdThirdRecordCode">商戶單號</label>
          <input
            id="wdThirdRecordCode"
            type="text"
            value={filters.thirdRecordCode}
            onChange={(e) => updateFilter('thirdRecordCode', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdType">提現方式</label>
          <select id="wdType" value={filters.type} onChange={(e) => updateFilter('type', e.target.value)}>
            <option value="">全部</option>
            {WITHDRAW_TYPE_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="wdThirdName">商戶接口</label>
          <input
            id="wdThirdName"
            type="text"
            value={filters.thirdName}
            onChange={(e) => updateFilter('thirdName', e.target.value)}
            placeholder="三方商戶名稱"
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdStatus">審核狀態</label>
          <select
            id="wdStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">全部</option>
            {WITHDRAW_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="wdRoleId">帳號類型</label>
          <select
            id="wdRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="wdTopInviterId">頂級上級用戶ID</label>
          <input
            id="wdTopInviterId"
            type="number"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdInviterId">直屬上級用戶ID</label>
          <input
            id="wdInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdMemberId">用戶ID</label>
          <input
            id="wdMemberId"
            type="number"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdPhoneEmail">用戶郵箱/手機號</label>
          <input
            id="wdPhoneEmail"
            type="text"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdCurrencyName">提現幣種</label>
          <input
            id="wdCurrencyName"
            type="text"
            value={filters.currencyName}
            onChange={(e) => updateFilter('currencyName', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdMinAmount">提現金額下限</label>
          <input
            id="wdMinAmount"
            type="number"
            step="0.01"
            value={filters.minWithdrawAmount}
            onChange={(e) => updateFilter('minWithdrawAmount', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdMaxAmount">提現金額上限</label>
          <input
            id="wdMaxAmount"
            type="number"
            step="0.01"
            value={filters.maxWithdrawAmount}
            onChange={(e) => updateFilter('maxWithdrawAmount', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdDepartmentId">部門別</label>
          <input
            id="wdDepartmentId"
            type="number"
            value={filters.departmentId}
            onChange={(e) => updateFilter('departmentId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdId">提現單號</label>
          <input
            id="wdId"
            type="number"
            value={filters.id}
            onChange={(e) => updateFilter('id', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdAddress">提現地址</label>
          <input
            id="wdAddress"
            type="text"
            value={filters.address}
            onChange={(e) => updateFilter('address', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdFirstWithdraw">是否首次提現</label>
          <select
            id="wdFirstWithdraw"
            value={filters.firstWithdraw}
            onChange={(e) => updateFilter('firstWithdraw', e.target.value)}
          >
            {FIRST_WITHDRAW_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="wdBehaviorNumber">取款次數</label>
          <input
            id="wdBehaviorNumber"
            type="number"
            min="1"
            value={filters.behaviorNumber}
            onChange={(e) => updateFilter('behaviorNumber', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdFirstBinding">提現地址首綁/修改</label>
          <select
            id="wdFirstBinding"
            value={filters.firstBindingOrModified}
            onChange={(e) => updateFilter('firstBindingOrModified', e.target.value)}
          >
            {FIRST_BINDING_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="wdCreateTimeFrom">申請時間起</label>
          <input
            id="wdCreateTimeFrom"
            type="datetime-local"
            value={filters.createTimeFromInput}
            onChange={(e) => updateFilter('createTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdCreateTimeTo">申請時間迄</label>
          <input
            id="wdCreateTimeTo"
            type="datetime-local"
            value={filters.createTimeToInput}
            onChange={(e) => updateFilter('createTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdCallbackTimeFrom">回調時間起</label>
          <input
            id="wdCallbackTimeFrom"
            type="datetime-local"
            value={filters.callbackTimeFromInput}
            onChange={(e) => updateFilter('callbackTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="wdCallbackTimeTo">回調時間迄</label>
          <input
            id="wdCallbackTimeTo"
            type="datetime-local"
            value={filters.callbackTimeToInput}
            onChange={(e) => updateFilter('callbackTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const statusCode =
                  typeof row.status === 'string' ? row.status : row.status?.name || row.status;
                const typeCode =
                  typeof row.type === 'string' ? row.type : row.type?.name || row.type;

                return (
                  <tr key={row.id}>
                    <td>{formatOptionalId(row.topInviterId)}</td>
                    <td className="mono-cell">{formatOptionalId(row.id)}</td>
                    <td className="mono-cell">{row.thirdRecordCode || '—'}</td>
                    <td>{formatWithdrawType(typeCode)}</td>
                    <td>{row.thirdName || '—'}</td>
                    <td className={withdrawStatusClassName(statusCode)}>
                      {formatWithdrawStatus(statusCode)}
                      {row.isLock ? '（已鎖）' : ''}
                    </td>
                    <td className="remark-cell">{row.failedInfo || '—'}</td>
                    <td className="remark-cell">{row.remark || '—'}</td>
                    <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                    <td>{formatOptionalId(row.inviterId)}</td>
                    <td>{formatOptionalId(row.memberId)}</td>
                    <td>{row.behaviorNumber ?? '—'}</td>
                    <td>{formatFirstWithdraw(row.behaviorRemark)}</td>
                    <td>{formatFirstBinding(row.firstBindingOrModified)}</td>
                    <td>{row.currencyName || '—'}</td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{formatAmount(row.fee)}</td>
                    <td>{formatAmount(row.withdrawalAmount)}</td>
                    <td>{formatAmount(row.receivedAmount)}</td>
                    <td className="mono-cell remark-cell">{row.address || '—'}</td>
                    <td>{formatDateTime(row.createTime)}</td>
                    <td>{formatDateTime(row.callbackDate)}</td>
                    <td>{formatOptionalId(row.departmentId)}</td>
                    <td>
                      <div className="table-actions table-actions--stack">
                        {canWithdrawLock(row) ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={actionId === row.id}
                            onClick={() => handleLock(row, true)}
                          >
                            {actionId === row.id ? '處理中…' : '鎖定'}
                          </button>
                        ) : null}
                        {canWithdrawUnlock(row) ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={actionId === row.id}
                            onClick={() => handleLock(row, false)}
                          >
                            {actionId === row.id ? '處理中…' : '解鎖'}
                          </button>
                        ) : null}
                        {canWithdrawPassReject(row) ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setPassRow(row)}
                            >
                              通過
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setRejectRow(row)}
                            >
                              拒絕
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setDetailRow(row)}
                        >
                          詳情
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setFeeDetailRow(row)}
                        >
                          手續費詳情
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            {!loading && records.length > 0 && (
              <tr className="summary-row">
                <td colSpan={11}>
                  合計 {total.recordCount ?? 0} 筆
                </td>
                <td colSpan={3} />
                <td>{formatAmount(total.amount)}</td>
                <td>{formatAmount(total.fee)}</td>
                <td>{formatAmount(total.withdrawalAmount)}</td>
                <td>{formatAmount(total.receivedAmount)}</td>
                <td colSpan={4} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <WithdrawPassModal
        open={Boolean(passRow)}
        row={passRow}
        onClose={() => setPassRow(null)}
        onSuccess={handleAuditSuccess}
      />
      <WithdrawRejectModal
        open={Boolean(rejectRow)}
        row={rejectRow}
        onClose={() => setRejectRow(null)}
        onSuccess={handleAuditSuccess}
      />
      <WithdrawDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
      <WithdrawFeeDetailModal
        open={Boolean(feeDetailRow)}
        row={feeDetailRow}
        onClose={() => setFeeDetailRow(null)}
      />
    </div>
  );
}
